import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowRight, ArrowUpRight, Search, Target, Users, Sparkles,
    Link2, Mail, Zap, ShieldCheck, Briefcase, Rocket, User, TrendingUp,
    ChevronDown, Play, Loader2, CheckCircle2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

// Marketing landing page rendered at "/" for un-authenticated visitors.
// Logged-in users are auto-redirected to /app.
// Aesthetic mirrors the in-app dark/zinc/amber palette so the marketing → app
// transition feels seamless. NO purple gradients, NO generic SaaS hero.

const FEATURES = [
    { n: "01", icon: Search, title: "Deep-dive research", body: "Type any company name. Get a complete dossier in 30 seconds — products, competitors, news, financials." },
    { n: "02", icon: Target, title: "Synergy scoring", body: "AI scores every pairing 0-100 for fit. Picks the BEST product to pitch. Projects revenue upside in $." },
    { n: "03", icon: Users, title: "Stakeholder map", body: "Know exactly who to email. AI maps 5 roles: decision-maker, champion, gatekeeper, budget owner, technical evaluator." },
    { n: "04", icon: Sparkles, title: "Stretch partners", body: "Companies you cannot sell to today, but would say YES after specific changes. Effort + timeframe scored." },
    { n: "05", icon: Link2, title: "Shareable pitches", body: "Mint a public pitch URL in one click. Paste in cold emails. No login wall — prospects see a slick deck instantly." },
    { n: "06", icon: Mail, title: "5-email cadence", body: "Pre-written drip sequence over 14 days. Value → proof → ROI → breakup → re-engagement. Just copy and send." },
];

const FLOW = [
    { step: "REQUEST", body: "Submit a firm name, a target match, or a stakeholder map. Add context in free-text notes." },
    { step: "DELIVERY", body: "Our analyst runs the AI on Claude Sonnet 4.5. You get a structured report — usually within 24 hours." },
    { step: "CLOSE", body: "Receive the pitch deck, stakeholder list, and 14-day email cadence. Export as PDF. Share the public pitch URL." },
];

const PERSONAS = [
    { icon: Briefcase, name: "BD / partnerships lead", body: "Run your discovery sessions in minutes, not weeks. Quintuple your reachable pipeline." },
    { icon: Rocket, name: "Founder / CEO", body: "Source strategic partners before your first hire. AI does the analyst work." },
    { icon: User, name: "Solo consultant", body: "Walk into pitches with branded dossiers, fit-scores, and revenue projections per client." },
    { icon: TrendingUp, name: "Sales rep", body: "Skip the cold-call grind. Get an 82-fit deal warm-loaded with a pitch deck before you dial." },
];

const FAQ = [
    { q: "How does it work?", a: "You submit a request — name a firm to research, two firms to analyze, or a target for stakeholder mapping. Our team runs the AI analysis on Claude Sonnet 4.5 and delivers a structured report within 24 hours. You review, ask for revisions, or export as PDF." },
    { q: "How much does it cost?", a: "Free during beta — unlimited requests, full reports, exported pitches. Paid tiers will launch post-beta with simple per-request or unlimited-month pricing. Beta users keep their data and get grandfathered pricing." },
    { q: "Which AI model powers Affynix?", a: "Claude Sonnet 4.5 (Anthropic) for research, synergy scoring, stakeholder maps, and outreach email drafting. Gemini Nano Banana generates product images. All run through the Emergent platform." },
    { q: "Is my data private?", a: "Yes. JWT auth, row-level isolation in MongoDB, no cross-account data leakage. Your requests and reports are only visible to you and the Affynix analyst handling them." },
    { q: "Does it work in Czech?", a: "Yes — toggle EN / CS in the toolbar. The AI generates dossiers, rationales, intro emails, and stretch-partner change-steps in your selected language." },
    { q: "Will my outreach emails be sent automatically?", a: "No — Affynix drafts them. You copy + paste into Gmail / Outlook / LinkedIn yourself. You're always in control of what hits the prospect's inbox." },
    { q: "Can I export my reports?", a: "Yes. Every delivered report is exportable as PDF (print-to-PDF from the result page) and as Markdown / CSV. You can also share a public pitch URL with prospects directly." },
];

// Illustrative early-beta voices. Replace with real customer quotes once they roll in.
const TESTIMONIALS = [
    {
        quote: "I used to spend four hours researching one prospect. Now I send Affynix a name before my morning coffee and have a full dossier by lunch.",
        name: "Marek J.",
        role: "BD consultant",
        city: "Prague",
    },
    {
        quote: "The stakeholder map alone is worth it. Knowing to email the Head of Platform first — not the VP Sales — changed my reply rate completely.",
        name: "Anna K.",
        role: "Founder, B2B SaaS",
        city: "Berlin",
    },
    {
        quote: "I dropped the public pitch URL into a cold email to a prospect's CFO. He forwarded it internally. We had a meeting four days later.",
        name: "Tomáš H.",
        role: "Partnership lead",
        city: "Vienna",
    },
];

// Mini-mockup data shown in the 'See it in action' preview cards. Hard-coded for visual fidelity.
const MOCK_SYNERGY = {
    a: "Stripe", b: "Fortell", fit: 82, revenue: "$8.5M",
    points: ["Embedded payouts in 8 weeks", "Multi-currency at one rate", "$8.5M ARR unlock"],
};

// LIVE DEMO — hard-coded showcase dossiers so anyone can preview the experience without signup.
// Costs $0 in LLM budget (purely client-side). The fallback dossier lets ANY input render a teaser
// so curious visitors never hit a dead end. Real research requires signup, surfaced as the CTA.
const DEMO_LIBRARY = {
    stripe: {
        name: "Stripe", industry: "Fintech · Payments", region: "US", hq: "South San Francisco, CA",
        founded: 2010, size: "8,000+", color: "#635BFF",
        overview: "Stripe builds economic infrastructure for the internet — payments, billing, treasury, and identity APIs used by millions of businesses worldwide. Best-in-class developer experience and aggressive international expansion make it the dominant horizontal payments platform.",
        competitors: ["Adyen", "Square", "Checkout.com", "PayPal"],
        news: ["Crossed $1T total payment volume in 2024", "Stripe Capital expanded into 7 EU markets", "Hired 600+ AI/ML engineers in the last 18 months"],
        products: [
            { name: "Payments", revenue: "$12.0B", growth: "+27%", share: "65%" },
            { name: "Connect (marketplaces)", revenue: "$2.5B", growth: "+41%", share: "14%" },
            { name: "Capital (lending)", revenue: "$420M", growth: "+88%", share: "2%" },
        ],
        themes: ["payments", "embedded-finance", "developer-first"],
    },
    notion: {
        name: "Notion", industry: "Productivity · SaaS", region: "US", hq: "San Francisco, CA",
        founded: 2013, size: "500+", color: "#000000",
        overview: "Notion is an all-in-one workspace for notes, docs, wikis, projects, and lightweight databases. Strong product-led growth, freemium funnel, and AI integration push it deep into enterprise from a startup foothold.",
        competitors: ["Coda", "Confluence", "Airtable", "Microsoft Loop"],
        news: ["Notion AI Q&A launched across all tiers", "$10B valuation reaffirmed in tender offer", "Notion Calendar (ex-Cron) crosses 1M MAU"],
        products: [
            { name: "Teams plan", revenue: "$300M", growth: "+42%", share: "60%" },
            { name: "Notion AI add-on", revenue: "$90M", growth: "+180%", share: "18%" },
            { name: "Enterprise", revenue: "$110M", growth: "+58%", share: "22%" },
        ],
        themes: ["productivity", "AI-native", "PLG"],
    },
    spotify: {
        name: "Spotify", industry: "Media · Streaming", region: "EU", hq: "Stockholm, Sweden",
        founded: 2006, size: "9,000+", color: "#1DB954",
        overview: "Spotify is the world's largest audio-streaming platform with 600M+ MAU. Aggressive push into podcasts, audiobooks, and creator monetization tools to broaden margin and reduce label dependency.",
        competitors: ["Apple Music", "YouTube Music", "Amazon Music", "Tidal"],
        news: ["Audiobooks bundled into Premium tier", "Marketplace tools now generate $200M+ ARR", "AI playlist generation rolled out globally"],
        products: [
            { name: "Premium subscriptions", revenue: "$11.5B", growth: "+15%", share: "88%" },
            { name: "Advertising", revenue: "$1.6B", growth: "+22%", share: "12%" },
        ],
        themes: ["audio", "B2C-subscription", "creator-economy"],
    },
    shopify: {
        name: "Shopify", industry: "E-commerce · SaaS", region: "Other", hq: "Ottawa, Canada",
        founded: 2006, size: "10,000+", color: "#95BF47",
        overview: "Shopify is a leading commerce platform giving merchants tools to start, run, and grow a multi-channel business. Strong Plus enterprise tier and Shop Pay network effects.",
        competitors: ["BigCommerce", "WooCommerce", "Wix", "Adobe Commerce"],
        news: ["Shopify Magic AI features expanded across admin", "Shop Pay accepted by 175M shoppers", "Editions launch ships 100+ features in one drop"],
        products: [
            { name: "Subscription Solutions", revenue: "$2.0B", growth: "+26%", share: "26%" },
            { name: "Merchant Solutions", revenue: "$5.5B", growth: "+24%", share: "70%" },
            { name: "Shop Pay", revenue: "$800M", growth: "+52%", share: "11%" },
        ],
        themes: ["commerce", "embedded-finance", "platform"],
    },
};
const DEMO_KEYS = Object.keys(DEMO_LIBRARY);

// Synthesize a teaser dossier for ANY freeform input — keeps the demo magical even for unknown firms.
function buildTeaserDossier(name) {
    const safe = (name || "Acme Co.").trim() || "Acme Co.";
    return {
        name: safe,
        industry: "Sample · Demo only",
        region: "—",
        hq: "—",
        founded: "—",
        size: "—",
        color: "#F59E0B",
        overview: `This is a demo teaser for ${safe}. In a real Affynix workspace, Claude Sonnet 4.5 builds a full dossier — overview, products with revenue, competitors, news, and tags — usually within 30 seconds. Sign up to run the real research on ${safe}.`,
        competitors: ["—", "—", "—"],
        news: [`(Demo) Real research would surface 3-5 recent moves by ${safe}.`, "(Demo) Funding rounds, leadership changes, product launches.", "(Demo) Competitor responses and market positioning shifts."],
        products: [
            { name: "Flagship product", revenue: "$—", growth: "+—%", share: "—" },
            { name: "Adjacent line", revenue: "$—", growth: "+—%", share: "—" },
        ],
        themes: ["sample", "demo", "preview-only"],
        teaser: true,
    };
}

// Self-contained accordion row for the FAQ section. State is local per item so they open independently.
function FAQItem({ q, a, idx }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={`faq-item-${idx}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full text-left flex items-center justify-between gap-4 py-5 group"
                data-testid={`faq-toggle-${idx}`}
                aria-expanded={open}
            >
                <span className="font-heading font-bold text-zinc-50 text-base group-hover:text-amber-400 transition-colors">{q}</span>
                <ChevronDown className={`w-4 h-4 shrink-0 text-zinc-500 group-hover:text-amber-400 transition-all ${open ? "rotate-180 text-amber-500" : ""}`} />
            </button>
            {open && (
                <div className="pb-5 pr-8 text-sm text-zinc-400 leading-relaxed" data-testid={`faq-body-${idx}`}>{a}</div>
            )}
        </div>
    );
}

// LIVE DEMO MODE — visitors type a firm name and see a teaser dossier render with a faux
// AI-status sequence. Hard-coded library covers 4 famous firms; anything else gets a sample
// "what real research would look like" placeholder. Zero LLM cost, zero backend dependency.
const DEMO_STAGES = [
    "Pulling public filings and registry records…",
    "Mapping product lines and pricing…",
    "Cross-referencing competitors and recent news…",
    "Estimating revenue and growth signals…",
    "Composing the dossier…",
];

function LiveDemo() {
    const [input, setInput] = useState("");
    const [running, setRunning] = useState(false);
    const [stageIdx, setStageIdx] = useState(0);
    const [result, setResult] = useState(null);
    const stageTimer = useRef(null);

    const start = (preset) => {
        const query = (preset || input).trim();
        if (!query) return;
        if (!preset) setInput(query);
        setResult(null);
        setRunning(true);
        setStageIdx(0);
        // Walk through 5 stages over ~3s total — keeps the demo snappy.
        let i = 0;
        const tick = () => {
            i += 1;
            if (i < DEMO_STAGES.length) {
                setStageIdx(i);
                stageTimer.current = setTimeout(tick, 520);
            } else {
                // resolve to dossier
                const key = query.toLowerCase().replace(/\s+/g, "");
                const match = DEMO_LIBRARY[key] || DEMO_LIBRARY[Object.keys(DEMO_LIBRARY).find((k) => key.includes(k)) || ""] || null;
                setResult(match || buildTeaserDossier(query));
                setRunning(false);
            }
        };
        stageTimer.current = setTimeout(tick, 520);
    };

    useEffect(() => () => stageTimer.current && clearTimeout(stageTimer.current), []);

    const reset = () => {
        if (stageTimer.current) clearTimeout(stageTimer.current);
        setInput("");
        setResult(null);
        setRunning(false);
        setStageIdx(0);
    };

    return (
        <section id="demo" className="border-t border-zinc-900/70 bg-zinc-950/30 py-24" data-testid="live-demo-section">
            <div className="max-w-5xl mx-auto px-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-3">// Try it · No signup needed</div>
                <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 max-w-3xl">
                    See a sample dossier in 3 seconds.
                </h2>
                <p className="text-zinc-400 text-base leading-relaxed max-w-2xl mt-3">
                    Type a name or pick a preset. We'll render a curated demo dossier — what every Affynix research request looks like.
                </p>

                {/* Search bar */}
                <div className="mt-8 border-2 border-zinc-800 focus-within:border-emerald-500 bg-black flex items-stretch transition-colors">
                    <div className="flex items-center pl-4 pr-3 border-r border-zinc-800">
                        <Search className="w-5 h-5 text-zinc-500" />
                    </div>
                    <input
                        data-testid="demo-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !running && start()}
                        placeholder="e.g. Stripe, Notion, Spotify, Shopify… or any company"
                        disabled={running}
                        className="flex-1 bg-transparent px-4 py-4 text-base text-zinc-100 placeholder:text-zinc-600 focus:outline-none font-mono disabled:opacity-60"
                    />
                    <button
                        type="button"
                        onClick={() => start()}
                        disabled={running || !input.trim()}
                        data-testid="demo-run-btn"
                        className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-xs uppercase tracking-wider px-6 flex items-center gap-2 transition-colors"
                    >
                        {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Running</> : <><Play className="w-4 h-4 fill-current" /> Run demo</>}
                    </button>
                </div>

                {/* Preset chips */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Or try:</span>
                    {DEMO_KEYS.map((k) => (
                        <button
                            key={k}
                            type="button"
                            disabled={running}
                            onClick={() => start(DEMO_LIBRARY[k].name)}
                            data-testid={`demo-preset-${k}`}
                            className="border border-zinc-800 hover:border-emerald-500 hover:text-emerald-400 text-xs font-mono text-zinc-300 px-2.5 py-1 transition-colors disabled:opacity-40"
                        >
                            {DEMO_LIBRARY[k].name}
                        </button>
                    ))}
                    {(result || running) && (
                        <button
                            type="button"
                            onClick={reset}
                            data-testid="demo-reset"
                            className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 transition-colors"
                        >
                            Reset
                        </button>
                    )}
                </div>

                {/* Running narrative */}
                {running && (
                    <div className="mt-6 border border-zinc-800 bg-black p-6" data-testid="demo-running">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-3">// Live AI status</div>
                        <ol className="space-y-2">
                            {DEMO_STAGES.map((s, i) => {
                                const done = i < stageIdx;
                                const active = i === stageIdx;
                                return (
                                    <li key={s} className={`flex items-center gap-2.5 text-sm transition-colors ${done ? "text-emerald-400" : active ? "text-zinc-100" : "text-zinc-700"}`}>
                                        {done
                                            ? <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
                                            : active
                                                ? <Loader2 className="w-4 h-4 shrink-0 animate-spin text-amber-500" />
                                                : <span className="w-4 h-4 inline-block border border-zinc-800 shrink-0" />}
                                        <span className="font-mono text-xs">{s}</span>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}

                {/* Result dossier */}
                {result && !running && (
                    <div className="mt-6 border border-zinc-800 bg-black animate-fade-in" data-testid="demo-result">
                        <div className="p-6 border-b border-zinc-800 flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="w-10 h-10 shrink-0 flex items-center justify-center font-mono font-black text-white text-sm" style={{ background: result.color }}>
                                    {result.name.charAt(0)}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1.5">
                                        // Dossier · Claude Sonnet 4.5 {result.teaser ? "· DEMO TEASER" : "· SAMPLE"}
                                    </div>
                                    <h3 className="font-heading font-black text-2xl text-zinc-50 truncate">{result.name}</h3>
                                    <div className="font-mono text-[11px] text-zinc-500 mt-1 truncate">
                                        {result.industry} · {result.hq} · Founded {result.founded} · {result.size}
                                    </div>
                                </div>
                            </div>
                            <Link
                                to="/signup"
                                data-testid="demo-cta-signup"
                                className="hidden sm:inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-[11px] uppercase tracking-wider px-3 py-2 transition-colors shrink-0"
                            >
                                Run real one <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>

                        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Overview</div>
                                <p className="text-sm text-zinc-300 leading-relaxed">{result.overview}</p>

                                <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Recent intel</div>
                                <ul className="space-y-1.5">
                                    {result.news.map((n, i) => (
                                        <li key={i} className="text-xs text-zinc-300 border-l-2 border-zinc-800 pl-3">{n}</li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Competitors</div>
                                <div className="flex flex-wrap gap-1.5 mb-4">
                                    {result.competitors.map((c, i) => (
                                        <span key={i} className="border border-zinc-700 bg-zinc-900/50 text-zinc-300 text-[11px] px-2 py-0.5">{c}</span>
                                    ))}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Themes</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {result.themes.map((t) => (
                                        <span key={t} className="border border-amber-500/40 bg-amber-500/10 text-amber-400 text-[11px] px-2 py-0.5">{t}</span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-zinc-800 p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Products · Money loop</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {result.products.map((p) => (
                                    <div key={p.name} className="border border-zinc-800 p-4 bg-black/40">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="font-heading font-bold text-zinc-50 text-sm">{p.name}</div>
                                            <div className="font-mono text-xs text-emerald-400">{p.growth}</div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
                                            <div>
                                                <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Revenue</div>
                                                <div className="text-amber-500">{p.revenue}</div>
                                            </div>
                                            <div>
                                                <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Share</div>
                                                <div className="text-zinc-300">{p.share}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border-t border-zinc-800 p-6 bg-zinc-950/50 flex items-center justify-between gap-4 flex-wrap">
                            <div className="font-mono text-[11px] text-zinc-500">
                                {result.teaser
                                    ? <>This is a placeholder. <span className="text-amber-400">Sign up</span> to run the real Claude Sonnet 4.5 research on <span className="text-zinc-200">{result.name}</span>.</>
                                    : <>Curated demo content. <span className="text-amber-400">Sign up</span> to run a fresh dossier on any firm of your choosing.</>}
                            </div>
                            <Link
                                to="/signup"
                                data-testid="demo-result-cta"
                                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wider px-4 py-2 transition-colors"
                            >
                                Start free <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default function Landing() {
    const { user } = useAuth();
    // user is null while bootstrapping, false when anon, object when authed
    if (user && typeof user === "object") return <Navigate to="/app" replace />;

    return (
        <div className="min-h-screen bg-black text-zinc-100 selection:bg-amber-500 selection:text-black" data-testid="landing-page">
            {/* HEADER */}
            <header className="border-b border-zinc-900/70 backdrop-blur-sm sticky top-0 z-40 bg-black/80">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5 group" data-testid="landing-logo">
                        <div className="w-8 h-8 bg-amber-500 flex items-center justify-center font-mono text-black font-black text-sm">A</div>
                        <div className="font-heading font-black tracking-tight text-zinc-50 text-base">AFFYNIX</div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-7 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                        <a href="#demo" className="hover:text-emerald-400 transition-colors">Try demo</a>
                        <a href="#features" className="hover:text-amber-400 transition-colors">Features</a>
                        <a href="#preview" className="hover:text-amber-400 transition-colors">Preview</a>
                        <a href="#how" className="hover:text-amber-400 transition-colors">How it works</a>
                        <a href="#voices" className="hover:text-amber-400 transition-colors">Voices</a>
                        <a href="#faq" className="hover:text-amber-400 transition-colors">FAQ</a>
                        <a href="#pricing" className="hover:text-amber-400 transition-colors">Pricing</a>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link to="/login" className="hidden sm:inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-400 transition-colors px-3 py-2" data-testid="landing-signin">
                            Sign in
                        </Link>
                        <Link to="/signup" className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-[11px] uppercase tracking-wider px-3.5 py-2 transition-colors" data-testid="landing-signup-header">
                            Start free <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(245,158,11,0.08),transparent_50%),radial-gradient(circle_at_80%_70%,rgba(245,158,11,0.05),transparent_50%)] pointer-events-none" />
                <div className="max-w-6xl mx-auto px-6 pt-20 pb-28 relative">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6">// Partnership intelligence</div>
                    <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-zinc-50 max-w-4xl">
                        <span className="text-4xl sm:text-5xl lg:text-6xl block">Map any firm.</span>
                        <span className="text-4xl sm:text-5xl lg:text-6xl block">Match it to the right partner.</span>
                        <span className="text-4xl sm:text-5xl lg:text-6xl block text-amber-500">We do the AI work for you.</span>
                    </h1>
                    <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl mt-7">
                        Submit a request — a firm to research, a partnership to score, a stakeholder map you need. Our analyst runs Claude Sonnet 4.5 and delivers a structured report within 24 hours. <span className="text-zinc-200">You stay focused on closing. We handle the research lift.</span>
                    </p>
                    <div className="flex items-center gap-3 mt-9 flex-wrap">
                        <Link
                            to="/signup"
                            data-testid="hero-cta-signup"
                            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm uppercase tracking-wider px-6 py-3.5 transition-all hover:-translate-y-0.5"
                        >
                            Submit your first request <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="#demo"
                            data-testid="hero-cta-demo"
                            className="inline-flex items-center gap-2 border border-emerald-500/60 hover:border-emerald-400 text-emerald-400 hover:text-emerald-300 font-mono text-xs uppercase tracking-wider px-6 py-3.5 transition-colors"
                        >
                            <Play className="w-3.5 h-3.5 fill-current" /> Try a live demo · no signup
                        </a>
                        <Link
                            to="/login"
                            data-testid="hero-cta-signin"
                            className="inline-flex items-center gap-2 border border-zinc-800 hover:border-amber-500 text-zinc-300 hover:text-amber-400 font-mono text-xs uppercase tracking-wider px-6 py-3.5 transition-colors"
                        >
                            Already on Affynix? Sign in
                        </Link>
                    </div>
                    {/* Stat bar */}
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800/60 border border-zinc-800/60" data-testid="landing-stats">
                        {[
                            { k: "<24h", v: "Avg turnaround" },
                            { k: "5 types", v: "Of request" },
                            { k: "PDF + URL", v: "Exportable" },
                            { k: "0 prompts", v: "You never write one" },
                        ].map((s) => (
                            <div key={s.k} className="bg-black p-5">
                                <div className="font-heading font-black text-2xl text-amber-500">{s.k}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.v}</div>
                            </div>
                        ))}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-5">
                        Powered by Claude Sonnet 4.5 · Gemini Nano Banana · Built on Emergent
                    </div>
                </div>
            </section>

            {/* LIVE DEMO — try before signup */}
            <LiveDemo />

            {/* FEATURES GRID */}
            <section id="features" className="border-t border-zinc-900/70 bg-zinc-950/30 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// What it does</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 max-w-3xl">
                        Six AI tools. One outbound machine.
                    </h2>
                    <p className="text-zinc-400 text-base leading-relaxed max-w-2xl mt-3">
                        Everything you need between "I just heard of this company" and "We just signed a partnership."
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800/60 border border-zinc-800/60 mt-12" data-testid="landing-features">
                        {FEATURES.map((f) => {
                            const Icon = f.icon;
                            return (
                                <article key={f.n} className="bg-black p-7 hover:bg-zinc-950 transition-colors group">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-10 h-10 border border-zinc-800 group-hover:border-amber-500 flex items-center justify-center transition-colors">
                                            <Icon className="w-4 h-4 text-amber-500" />
                                        </div>
                                        <div className="font-mono text-[10px] text-zinc-700 tracking-wider">{f.n}</div>
                                    </div>
                                    <h3 className="font-heading font-bold text-zinc-50 text-lg mb-2">{f.title}</h3>
                                    <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="border-t border-zinc-900/70 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// How it works</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 max-w-3xl">
                        Three steps. Two minutes. Done.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800/60 border border-zinc-800/60 mt-12" data-testid="landing-flow">
                        {FLOW.map((s, i) => (
                            <div key={s.step} className="bg-black p-7 relative">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3">
                                    Step {String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="font-heading font-black text-2xl text-amber-500 mb-3 tracking-tight">{s.step}</h3>
                                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                                {i < FLOW.length - 1 && (
                                    <ArrowRight className="hidden md:block absolute right-[-12px] top-7 w-5 h-5 text-amber-500 bg-black border border-amber-500/40 p-0.5 z-10" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SEE IT IN ACTION — mockup previews of actual app screens */}
            <section id="preview" className="border-t border-zinc-900/70 bg-zinc-950/30 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// See it in action</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 max-w-3xl">
                        What the AI hands you, every time.
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-12" data-testid="landing-preview">
                        {/* Mockup A — Synergy result card */}
                        <div className="border border-zinc-800 bg-black p-5">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-2">// SYNERGY · 0.7s</div>
                            <div className="font-heading font-black text-xl text-zinc-50 leading-tight">{MOCK_SYNERGY.a} <span className="text-amber-500">×</span> {MOCK_SYNERGY.b}</div>
                            <div className="grid grid-cols-2 gap-px bg-zinc-800 mt-3 border border-zinc-800">
                                <div className="bg-black p-2.5">
                                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">Fit score</div>
                                    <div className="font-heading font-black text-2xl text-amber-500">{MOCK_SYNERGY.fit}<span className="text-xs text-zinc-600">/100</span></div>
                                </div>
                                <div className="bg-black p-2.5">
                                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">Revenue / yr</div>
                                    <div className="font-heading font-black text-2xl text-emerald-400">{MOCK_SYNERGY.revenue}</div>
                                </div>
                            </div>
                            <ol className="mt-3 space-y-1.5">
                                {MOCK_SYNERGY.points.map((p, i) => (
                                    <li key={i} className="flex gap-2 text-xs text-zinc-300 leading-relaxed">
                                        <span className="font-mono text-amber-500 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                        {p}
                                    </li>
                                ))}
                            </ol>
                        </div>

                        {/* Mockup B — Stakeholder map */}
                        <div className="border border-zinc-800 bg-black p-5">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-400 mb-2">// STAKEHOLDERS · 5 roles</div>
                            <div className="font-heading font-bold text-base text-zinc-50 mb-3">Who to email at Fortell</div>
                            <ul className="space-y-1.5">
                                {[
                                    { role: "Decision maker", title: "VP of Engineering", tone: "border-amber-500/50 bg-amber-500/10 text-amber-300" },
                                    { role: "Champion", title: "Head of Platform", tone: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" },
                                    { role: "Gatekeeper", title: "Chief of Staff to CEO", tone: "border-blue-500/50 bg-blue-500/10 text-blue-300" },
                                    { role: "Budget owner", title: "CFO", tone: "border-purple-500/50 bg-purple-500/10 text-purple-300" },
                                    { role: "Tech evaluator", title: "Staff Eng, Payments", tone: "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" },
                                ].map((s) => (
                                    <li key={s.role} className="flex items-center justify-between gap-2 text-xs">
                                        <span className={`font-mono text-[9px] uppercase tracking-wider border px-1.5 py-0.5 ${s.tone}`}>{s.role}</span>
                                        <span className="text-zinc-300 truncate">{s.title}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Mockup C — Email sequence preview */}
                        <div className="border border-zinc-800 bg-black p-5">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-2">// EMAIL CADENCE · 14 days</div>
                            <div className="font-heading font-bold text-base text-zinc-50 mb-3">5 emails, pre-written</div>
                            <ol className="space-y-2">
                                {[
                                    { d: "Day 0", t: "VALUE", s: "Multi-currency payouts for Fortell?" },
                                    { d: "Day +3", t: "PROOF", s: "How Shopify handles 2M+ seller payouts" },
                                    { d: "Day +7", t: "ROI", s: "$8.5M ARR unlock with embedded payments" },
                                    { d: "Day +11", t: "BREAKUP", s: "Should I close the loop?" },
                                    { d: "Day +14", t: "RE-ENGAGE", s: "Supplier payment benchmarks (no strings)" },
                                ].map((e, i) => (
                                    <li key={i} className="border-l-2 border-amber-500/60 pl-2.5 py-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[9px] uppercase tracking-wider text-amber-400">{e.d}</span>
                                            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">· {e.t}</span>
                                        </div>
                                        <div className="text-xs text-zinc-200 truncate font-heading">{e.s}</div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                    <div className="text-center mt-10">
                        <Link
                            to="/signup"
                            data-testid="preview-cta-signup"
                            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-amber-500 hover:text-amber-400 border-b border-amber-500/40 hover:border-amber-400 pb-0.5 transition-colors"
                        >
                            Try it on your first firm <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR — personas */}
            <section id="personas" className="border-t border-zinc-900/70 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// Built for</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 max-w-3xl">
                        Anyone whose job is "find me a partner."
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800/60 border border-zinc-800/60 mt-12" data-testid="landing-personas">
                        {PERSONAS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <div key={p.name} className="bg-black p-6">
                                    <Icon className="w-6 h-6 text-amber-500 mb-4" />
                                    <div className="font-heading font-bold text-zinc-50 text-base mb-2">{p.name}</div>
                                    <div className="text-xs text-zinc-400 leading-relaxed">{p.body}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS — early-beta voices */}
            <section id="voices" className="border-t border-zinc-900/70 py-24">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// Voices from the early beta</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 max-w-3xl">
                        What people are saying.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12" data-testid="landing-testimonials">
                        {TESTIMONIALS.map((t, i) => (
                            <figure key={i} className="border border-zinc-800 bg-black p-6 flex flex-col" data-testid={`testimonial-${i}`}>
                                <div className="font-mono text-4xl text-amber-500/40 leading-none mb-3" aria-hidden="true">&ldquo;</div>
                                <blockquote className="text-sm text-zinc-200 leading-relaxed flex-1">{t.quote}</blockquote>
                                <figcaption className="mt-4 pt-4 border-t border-zinc-800/60">
                                    <div className="font-heading font-bold text-zinc-50 text-sm">{t.name}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{t.role} · {t.city}</div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                    <p className="text-center text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-600 mt-8">
                        Early-access feedback · names changed for privacy
                    </p>
                </div>
            </section>

            {/* FAQ — accordion */}
            <section id="faq" className="border-t border-zinc-900/70 bg-zinc-950/30 py-24">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// FAQ</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50 mb-10">
                        Questions, answered.
                    </h2>
                    <div className="divide-y divide-zinc-800/60 border-y border-zinc-800/60" data-testid="landing-faq">
                        {FAQ.map((item, i) => (
                            <FAQItem key={i} q={item.q} a={item.a} idx={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CREDIBILITY STRIP */}
            <section className="border-t border-zinc-900/70 bg-zinc-950/30 py-16">
                <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800/60 border border-zinc-800/60">
                    {[
                        { icon: Zap, k: "Built for speed", v: "Background AI jobs · polling pattern · no 30s timeout" },
                        { icon: ShieldCheck, k: "Your data, your workspace", v: "Multi-user · JWT auth · row-level isolation" },
                        { icon: Sparkles, k: "Multilingual AI", v: "English / Czech locale toggle — Claude switches output language" },
                    ].map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.k} className="bg-black p-7">
                                <Icon className="w-5 h-5 text-amber-500 mb-3" />
                                <div className="font-heading font-bold text-zinc-50 text-base">{c.k}</div>
                                <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{c.v}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* PRICING TEASER */}
            <section id="pricing" className="border-t border-zinc-900/70 py-24">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// Pricing</div>
                    <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50">
                        Free during beta.
                    </h2>
                    <p className="text-zinc-400 text-base leading-relaxed mt-3 max-w-xl mx-auto">
                        Build your first 5 firms, run your first 10 synergies, share your first pitch — all on the house. Paid tiers launching post-beta.
                    </p>
                    <div className="mt-8">
                        <Link
                            to="/signup"
                            data-testid="pricing-cta-signup"
                            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm uppercase tracking-wider px-6 py-3.5 transition-all hover:-translate-y-0.5"
                        >
                            Claim your workspace <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="border-t border-zinc-900/70 py-10 bg-black">
                <div className="max-w-6xl mx-auto px-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 bg-amber-500 flex items-center justify-center font-mono text-black font-black text-xs">A</div>
                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Affynix · © 2026</div>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                        Made with Claude Sonnet 4.5 · Hosted on Emergent
                    </div>
                </div>
            </footer>
        </div>
    );
}
